import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkStatus, useApolloClient } from '@apollo/client';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useFakturowniaSyncStatusQuery,
  useRunFakturowniaSyncMutation,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { IFakturowniaSyncStats } from './types';
import { evictFinanceLists } from './evictFinanceLists';

/**
 * Hard cap on how long we poll the status endpoint after the user clicks
 * «Sync now». Backend syncs usually finish in under a minute, but if the
 * task queue stalls we stop polling rather than hammering forever.
 */
const POLL_INTERVAL_MS = 2500;
const SYNC_TIMEOUT_MS = 5 * 60 * 1000;

interface IFakturowniaSyncHook {
  /** Last status snapshot (status + lastRun + lastSuccess). */
  status?: ReturnType<typeof useFakturowniaSyncStatusQuery>['data'];
  /** Quick badge — number of issued invoices waiting for a student. */
  unmatchedCount: number;
  /** True while the backend is running or our trigger is queued. */
  syncing: boolean;
  loading: boolean;
  /** Kicks off `triggerFakturowniaSync` and starts polling status. */
  triggerSync: () => void;
  refetch: () => void;
}

/**
 * Wraps the «sync widget» from TZ §3.1: one query for status + unmatched
 * count, one mutation to enqueue. We poll status while the backend reports
 * `isRunning=true`; on completion the caller's `onSynced` callback runs so
 * the page can refresh its data.
 */
export const useFakturowniaSync = (
  onSynced?: () => void,
): IFakturowniaSyncHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const client = useApolloClient();

  const [syncing, setSyncing] = useState(false);
  const startedAtRef = useRef(0);
  const prevNetworkStatusRef = useRef<NetworkStatus>();
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const {
    data,
    loading,
    networkStatus,
    refetch,
  } = useFakturowniaSyncStatusQuery({
    pollInterval: syncing ? POLL_INTERVAL_MS : 0,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [runSync] = useRunFakturowniaSyncMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.FakturowniaSync,
      });
    },
    [addToast],
  );

  const triggerSync = useCallback(() => {
    if (syncing) {
      return;
    }
    startedAtRef.current = Date.now();
    prevNetworkStatusRef.current = undefined;

    runSync({ variables: { period: 'last_30_days' } })
      .then((result) => {
        const payload = result.data?.triggerFakturowniaSync;
        if (!payload) {
          return;
        }
        if (!payload.enqueued && payload.message) {
          // Coalesced — surface the explanation but stay in idle.
          notify(ToastTypes.warning, payload.message);
          // Still enter polling mode in case the existing run hasn't drained.
        }
        setSyncing(true);
      })
      .catch((error: Error) => {
        notify(
          ToastTypes.error,
          t('pages.fakturownia.sync.errorTitle'),
          error.message,
        );
      });
  }, [syncing, runSync, notify, t]);

  // React to status transitions — once `isRunning` flips back to false we
  // stop polling, toast the result and let the caller refresh its data.
  useEffect(() => {
    if (!syncing) {
      return;
    }

    const justReady =
      networkStatus === NetworkStatus.ready &&
      prevNetworkStatusRef.current !== NetworkStatus.ready;
    prevNetworkStatusRef.current = networkStatus;
    if (!justReady) {
      return;
    }

    const status = data?.fakturowniaSyncStatus;
    if (!status) {
      return;
    }

    if (Date.now() - startedAtRef.current > SYNC_TIMEOUT_MS) {
      notify(ToastTypes.warning, t('pages.fakturownia.sync.timeoutTitle'));
      setSyncing(false);
      return;
    }

    if (status.isRunning) {
      return;
    }

    setSyncing(false);

    const { lastRun } = status;
    if (lastRun?.status && /failed/i.test(lastRun.status)) {
      notify(
        ToastTypes.error,
        t('pages.fakturownia.sync.errorTitle'),
        lastRun.errorMessage || undefined,
      );
      return;
    }

    let stats: IFakturowniaSyncStats | undefined;
    if (lastRun?.stats) {
      try {
        stats =
          typeof lastRun.stats === 'string'
            ? (JSON.parse(lastRun.stats) as IFakturowniaSyncStats)
            : (lastRun.stats as IFakturowniaSyncStats);
      } catch {
        stats = undefined;
      }
    }
    notify(
      ToastTypes.success,
      t('pages.fakturownia.sync.doneTitle', {
        invoices: stats?.invoices ?? 0,
        expenses: stats?.expenses ?? 0,
        payments: stats?.payments_upserted ?? 0,
      }),
    );

    // Phase 13: orphan payments (mirror /payments.json receipts that don't
    // belong to any invoice) deserve a separate warning — they mean
    // something fell out of the Payment→Invoice chain on Fakturownia's side.
    if (stats?.payments_orphaned && stats.payments_orphaned > 0) {
      notify(
        ToastTypes.warning,
        t('pages.fakturownia.sync.orphanWarning', {
          count: stats.payments_orphaned,
        }),
      );
    }

    // A sync upserts invoices AND expenses server-side — every cached drain
    // combo (any date preset, any group/student card) is stale now. Evict
    // them all so post-sync data is reachable everywhere, not only on the
    // screen that happened to pass onSynced (finance-block audit).
    evictFinanceLists(client.cache);
    onSyncedRef.current?.();
  }, [data, networkStatus, syncing, notify, t, client]);

  return {
    status: data,
    unmatchedCount: data?.fakturowniaUnmatchedCount ?? 0,
    syncing: syncing || Boolean(data?.fakturowniaSyncStatus?.isRunning),
    loading,
    triggerSync,
    refetch: () => {
      refetch();
    },
  };
};
